import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { api, apiError } from "../../lib/api";
import { PageHeader, Card, Table, Td, Badge, Empty, Btn, Modal } from "../../components/UI";
import { ClinicalTabs, useClients } from "./shared";
import { FSelect } from "../engagement/FormControls";
import { Plus, Pencil, Trash2, Lock, Check, Timer, CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import { VersionAccordion } from "../../components/VersionAccordion";

const TP_COLS = 10;

export default function TreatmentPlans() {
  const navigate = useNavigate();
  const clients = useClients();
  const [plans, setPlans] = useState([]);
  const [clientFilter, setClientFilter] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    const params = clientFilter ? { client_id: clientFilter } : {};
    api.get("/treatment-plans", { params }).then((r) => { setPlans(r.data); setLoading(false); });
  };
  useEffect(load, [clientFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const doDelete = async () => {
    try {
      await api.delete(`/treatment-plans/${confirmDelete.id}`);
      toast.success("Treatment plan deleted");
      setConfirmDelete(null); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const SigDot = ({ ok, label }) => (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${ok ? "text-[#2E7D32]" : "text-[#84675B]"}`}>
      <span className={`flex h-4 w-4 items-center justify-center rounded-full ${ok ? "bg-[#E8F5E9]" : "bg-[#F5F2EB]"}`}>{ok && <Check size={10} />}</span>{label}
    </span>
  );

  const ReviewTimer = ({ p }) => {
    if (!p.review_date) return <span className="text-xs text-[#84675B]">-</span>;
    const days = dayjs(p.review_date).diff(dayjs().startOf("day"), "day");
    const cls = days < 0 ? "bg-[#FFEBEE] text-[#C62828]" : days <= 14 ? "bg-[#FFF3E0] text-[#B84A00]" : "bg-[#E8F5E9] text-[#2E7D32]";
    const label = days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? "Due today" : `${days}d left`;
    return (
      <span data-testid={`tp-review-timer-${p.id}`} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold ${cls}`}>
        <Timer size={11} /> {label}
      </span>
    );
  };

  const markReviewed = async (p) => {
    try {
      const r = await api.put(`/treatment-plans/${p.id}/review`, {});
      toast.success(`Plan reviewed - next review ${dayjs(r.data.review_date).format("MM/DD/YYYY")}`);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div>
      <PageHeader title="Treatment Plans" subtitle="Problems → goals → objectives → interventions"
        action={<Btn data-testid="add-treatment-plan-btn" onClick={() => navigate("/clinical/treatment-plans/new")}><Plus size={15} /> New Treatment Plan</Btn>} />
      <ClinicalTabs />
      <Card>
        <div className="flex flex-wrap gap-3 p-4">
          <FSelect data-testid="tp-client-filter" className="w-56" placeholder="All Clients" value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
          </FSelect>
        </div>
        <Table headers={["", "Title", "Client", "Diagnosis", "Start", "Review Due", "Review Timer", "Status", "Signatures", ""]}>
          {plans.map((p) => (
            <VersionAccordion key={p.id} record={p} kind="treatment_plan" colSpan={TP_COLS}
              primaryTestId={`tp-row-${p.id}`} rowClassName="hover:bg-[#FDFCF8] transition-colors"
              renderPrimary={({ expandToggle, versionBadge }) => (
                <>
                  <Td className="w-8">{expandToggle}</Td>
                  <Td className="font-medium text-[#3E2723] cursor-pointer" onClick={() => navigate(`/clinical/treatment-plans/${p.id}`)}>
                    <span className="flex items-center gap-1.5">{p.title}{versionBadge}</span>
                  </Td>
                  <Td className="cursor-pointer" onClick={() => navigate(`/clinical/treatment-plans/${p.id}`)}>{p.client_name}</Td>
                  <Td className="max-w-[220px] truncate">{p.diagnosis || "-"}</Td>
                  <Td>{p.start_date ? dayjs(p.start_date).format("MM/DD/YYYY") : "-"}</Td>
                  <Td>{p.review_date ? dayjs(p.review_date).format("MM/DD/YYYY") : "-"}</Td>
                  <Td><ReviewTimer p={p} /></Td>
                  <Td><Badge value={p.status} /></Td>
                  <Td><div className="flex gap-3"><SigDot ok={!!p.clinician_signature} label="Clinician" /><SigDot ok={!!p.client_signature} label="Client" /></div></Td>
                  <Td>
                    <div className="flex gap-2 justify-end">
                      <button type="button" data-testid={`tp-review-${p.id}`} aria-label="Mark reviewed" title="Mark reviewed (+90 days)" onClick={() => markReviewed(p)}
                        className="h-8 w-8 rounded-full bg-[#E8F5E9] text-[#2E7D32] flex items-center justify-center hover:bg-[#C8E6C9]"><CalendarCheck size={13} /></button>
                      <button type="button" data-testid={`tp-open-${p.id}`} aria-label="Open" onClick={() => navigate(`/clinical/treatment-plans/${p.id}`)}
                        className="h-8 w-8 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center hover:bg-[#EDE7DC]">
                        {p.clinician_signature ? <Lock size={13} /> : <Pencil size={13} />}
                      </button>
                      {!p.clinician_signature && (
                        <button type="button" data-testid={`tp-delete-${p.id}`} aria-label="Delete" onClick={() => setConfirmDelete(p)}
                          className="h-8 w-8 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center hover:bg-[#FFEBEE] hover:text-[#C62828]"><Trash2 size={13} /></button>
                      )}
                    </div>
                  </Td>
                </>
              )}
            />
          ))}
        </Table>
        {!loading && plans.length === 0 && <Empty text="No treatment plans yet" />}
      </Card>

      <Modal open={!!confirmDelete} onClose={() => setConfirmDelete(null)} title="Delete Treatment Plan">
        <p className="text-sm text-[#6D4C41] mb-5">Delete "<span className="font-semibold">{confirmDelete?.title}</span>" for {confirmDelete?.client_name}?</p>
        <div className="flex justify-end gap-2">
          <Btn variant="ghost" onClick={() => setConfirmDelete(null)}>Cancel</Btn>
          <Btn data-testid="tp-confirm-delete-btn" variant="danger" onClick={doDelete}>Delete</Btn>
        </div>
      </Modal>
    </div>
  );
}
